import { IntegrationData } from "../types"

export const gripData: IntegrationData = {
  platform: {
    slug: "grip",
    name: "Grip",
    description:
      "Native Premagic integration built directly into Grip's platform for one-click poster activation",
    shortDescription: "Native integration for event management",
    logoUrl:
      "https://cdn.prod.website-files.com/5fb3c15a27ce48001c749db9/651bd8f25d1d847ed18d275a_Grip-Webicon-v2.png",
    websiteUrl: "https://www.grip.events/",
    category: "event-management",
    tags: ["Event Management", "Native Integration"],
    isComingSoon: false,
  },
  heroTitle:
    "Native Premagic Integration in Grip – One-Click Poster Activation",
  heroDescription:
    "Premagic is now natively integrated into Grip, the leading event networking and engagement platform. This isn't just another API connection – Grip has built Premagic directly into their platform, making it easier than ever to turn your event registrations into a referral growth engine. No code. No developers. Just toggle it on.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You've set up your event on Grip. Registrations are coming in. But how do you turn that momentum into explosive growth? The most powerful driver of event registrations has always been word-of-mouth – attendees sharing with their networks. The moment after registration is when they're most excited and most likely to share.",
  challengePoints: [
    {
      title: "Limited Organic Reach",
      description:
        "Most attendees don't actively promote your event, even if they're excited about it. They need a nudge. They need something shareable. They need it to be effortless.",
    },
    {
      title: "Missed Peak Excitement Window",
      description:
        "The moment someone registers is when they're most excited about your event. Without a tool embedded in your registration flow, you miss this critical window to capture referrals.",
    },
    {
      title: "Complex Setup Requirements",
      description:
        "Traditional integrations require API keys, webhooks, and technical setup. Event organizers shouldn't need developers just to activate a referral engine.",
    },
  ],
  capabilities: [
    {
      title: "Automatic Poster Widget on Registration Pages",
      description:
        "Once enabled, the Premagic widget appears seamlessly within your Grip registration experience. No iframe embedding – native integration means a smooth user experience.",
      features: [
        "Native integration means zero friction",
        "Branded to your event's look and feel",
        "Mobile-optimized for any device",
        "Attendees create posters without leaving the flow",
      ],
    },
    {
      title: "Capture Attendees at Peak Excitement",
      description:
        "The moment someone registers is when they're most excited about your event. The native widget captures that moment and turns it into immediate sharing.",
      features: [
        "Registration complete → Poster widget appears → Attendee shares immediately",
        "No follow-up emails needed to drive initial sharing",
        "Conversion happens in the same session",
        "Maximize the window of peak engagement",
      ],
    },
    {
      title: "Pre-filled Personalized Posters",
      description:
        "Because the widget is integrated into the registration flow, attendee data flows directly into poster creation. Names, event details, and more are automatically populated.",
      features: [
        "Name automatically populated",
        "Event details pre-filled",
        "One-click poster generation",
        "Instant social sharing options",
      ],
    },
    {
      title: "Drive Registrations Through Referrals",
      description:
        "Every poster shared is a potential new registration. Attendees share to LinkedIn, Twitter, WhatsApp, and more. Their networks see personalized, branded event content.",
      features: [
        "Attendees share to LinkedIn, Twitter, WhatsApp, and more",
        "Their networks see personalized, branded event content",
        "New registrations driven by social proof and peer recommendations",
        "Track referral performance and top promoters",
      ],
    },
    {
      title: "Flexible Widget Configuration",
      description:
        "The integration supports multiple configuration options to match your specific event needs and goals.",
      features: [
        "Choose poster_creation or other flows based on your goals",
        "Segment by attendee categories",
        "Match the display to your registration page design",
        "Customize widget behavior and appearance",
      ],
    },
  ],
  flywheelTitle: "The Registration-to-Referral Flow",
  flywheelDescription:
    "Here's what happens when Premagic is enabled in Grip. Every registration fuels the next one.",
  flywheelSteps: [
    {
      number: 1,
      title: "Register",
      description: "Attendee completes Grip registration form",
    },
    {
      number: 2,
      title: "Widget Appears",
      description: "Premagic poster creator loads automatically",
    },
    {
      number: 3,
      title: "Personalize",
      description: "Attendee sees their name on a beautiful event poster",
    },
    {
      number: 4,
      title: "Create",
      description: "One click to generate their personalized poster",
    },
    {
      number: 5,
      title: "Share",
      description:
        "Direct sharing to LinkedIn, Twitter, Facebook, WhatsApp, email",
    },
    {
      number: 6,
      title: "Amplify",
      description: "Their network sees the event and registers",
    },
    {
      number: 7,
      title: "Repeat",
      description:
        "New registrants see the widget too – every registration fuels the next one",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Navigate to Integrations",
      description:
        "In your Grip admin panel, go to Event Settings → Integrations → Grip Engage and select Premagic",
    },
    {
      number: 2,
      title: "Configure Your Settings",
      description:
        "Fill in your Premagic project details: Share Id, Project Id, Event Id, Website Id, Domain, and Widget preferences",
    },
    {
      number: 3,
      title: "Enable and Save",
      description: "Toggle Enabled to on, click Save, and you're done",
    },
    {
      number: 4,
      title: "Watch the Magic Happen",
      description:
        "The Premagic poster creation widget automatically appears on your Grip registration page",
    },
    {
      number: 5,
      title: "Track and Optimize",
      description:
        "Monitor poster creation rates, shares, and referral registrations in your Grip dashboard",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Automatic Poster Widget",
      description:
        "Native widget appears on registration pages with zero setup required",
    },
    {
      feature: "Peak Excitement Capture",
      description:
        "Engage attendees at the moment they're most excited to share",
    },
    {
      feature: "Pre-filled Posters",
      description:
        "Attendee data automatically populates personalized poster templates",
    },
    {
      feature: "Referral Activation",
      description:
        "Turn every registration into a potential referral engine through social sharing",
    },
    {
      feature: "Flexible Configuration",
      description:
        "Customize widget flow, attendee types, and display styles to match your event",
    },
    {
      feature: "Native Integration",
      description:
        "Built directly into Grip's platform – no API keys, webhooks, or technical setup",
    },
    {
      feature: "One-Click Activation",
      description:
        "Enable Premagic in minutes without developers or technical knowledge",
    },
    {
      feature: "Seamless Experience",
      description:
        "Attendees create and share posters without leaving the registration flow",
    },
  ],
  targetAudience: [
    {
      title: "Grip Event Organizers",
      description:
        "Who want to maximize registration growth through attendee advocacy",
    },
    {
      title: "Event Marketing Teams",
      description:
        "Looking for effortless referral activation without technical complexity",
    },
    {
      title: "Exhibition Managers",
      description:
        "Driving exhibitor and attendee registrations for large-scale events",
    },
    {
      title: "Conference Producers",
      description:
        "Building buzz before the event through word-of-mouth amplification",
    },
  ],
  ctaTitle: "Ready to Activate Premagic in Your Grip Events?",
  ctaDescription:
    "The Grip + Premagic native integration removes every barrier between your registrations and referral growth. No code to write. No widgets to embed. No developers needed. Just toggle it on, and every registration becomes a potential referral engine. Your attendees want to share. Now there's nothing stopping them.",
  seo: {
    title: "Premagic + Grip Integration | One-Click Poster Activation",
    description:
      "Native Premagic integration built directly into Grip's platform. Enable one-click poster activation, capture attendees at peak excitement, and drive event registrations through referrals – no code required.",
    keywords: [
      "grip",
      "event management",
      "native integration",
      "attendee advocacy",
      "event marketing",
      "referral marketing",
      "poster creation",
      "registration growth",
      "event networking",
    ],
  },
}
